const gridCss = `
    .form-picker-grid { 
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); 
      gap: 12px; 
    }
    .form-picker-card { 
      cursor: pointer; 
      border: 1px solid #ddd; 
      border-radius: 8px; 
      overflow: hidden; 
      background: #fff; 
    }
    .form-picker-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,.12) }
    .form-picker-card img { width: 100%; height: 260px; object-fit: contain; display: block }
    .form-picker-card .fp-title {
      padding: 8px 10px;
      font-weight: 600;
      font-size: 14px;
      line-height: 1.3;
      color: #111;           
      display: block;        
    }
    .gjs-mdl-dialog { max-width: 950px; width: 90vw; }
    @media (max-width: 768px) {
      .form-picker-grid { 
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); 
        gap: 8px; 
      }
      .gjs-mdl-dialog { max-width: 95vw; width: 95vw; }
    }
  `;

const formCard = (t: any) => `
      <div class="form-picker-card" data-id="${t.id}">
        <img src="${t.thumbnail}" alt="${t.name}">
        <div class="fp-title">${t.name}</div>
      </div>
    `;

const formCache = new Map<string, string>();

async function getFormHtml(tpl: { url?: string; component?: string }) {
  if (!tpl.url) return '';
  if (formCache.has(tpl.url)) return formCache.get(tpl.url)!;
  const res = await fetch(tpl.url, { credentials: 'same-origin' }); // same-origin -> no CORS issues
  if (!res.ok) throw new Error(`Failed to load ${tpl.url}: ${res.status}`);
  const html = await res.text();
  formCache.set(tpl.url, html);
  return html;
}

// Inside the openFormPickerModal function, where the form is inserted:
export function openFormPickerModal(
  ed: any,
  opts: { placeholderId?: string } = {},
  FORM_TEMPLATES: any,
  websiteId: string
) {
  const cards = FORM_TEMPLATES.map((t: any) => formCard(t)).join('');

  const body = document.createElement('div');
  body.innerHTML = `
    <style>${gridCss}</style>
    <div class="form-picker-grid">${cards}</div>
  `;

  const modal = ed.Modal;
  modal.setTitle('Choose a form');
  modal.setContent(body);
  modal.open();

  body.addEventListener('click', async e => {
    const card = (e.target as HTMLElement).closest('.form-picker-card') as HTMLElement | null;
    if (!card) return;

    const tpl = FORM_TEMPLATES.find((f: any) => f.id === card.getAttribute('data-id'));
    if (!tpl) return;

    const old = body.innerHTML;
    body.innerHTML = `<div>Loading ${tpl.name}…</div>`;

    try {
      // 1) Fetch raw HTML for the form
      const rawHtml = await getFormHtml(tpl);

      // 2) Add website id attribute + hidden input into the <form>
      const siteId = websiteId || 'YOUR_WEBSITE_ID';
      const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
      const inlineCss = Array.from(doc.querySelectorAll('style'))
        .map(s => s.textContent || '')
        .join('\n');

      if (inlineCss.trim()) {
        // Adds CSS rules into the current project (shows up in the Style Manager export)
        ed.addStyle(inlineCss);
      }
      const formEl = doc.querySelector('form');

      if (formEl) {
        // Attribute for client-side logic / selectors
        formEl.setAttribute('data-website-id', siteId);

        // Hidden field to submit to your backend
        const hidden = doc.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'website_id';
        hidden.value = siteId;
        formEl.appendChild(hidden);
      }

      // Stringified HTML after mutation
      const html = doc.body.innerHTML;

      // 3) Insert into GrapesJS
      const wrapper = ed.getWrapper();

      // Case A: replace a placeholder by id (keeping position)
      if (opts.placeholderId) {
        const [ph] = wrapper.find(`#${opts.placeholderId}`);
        if (ph) {
          const parent = ph.parent();
          const at = parent ? parent.index(ph) : undefined;
          if (parent && typeof at === 'number') {
            parent.append(html, { at });
            ph.remove();

            // Sync GrapesJS component model attributes
            const [formComp] = wrapper.find('form');
            formComp?.addAttributes({ 'data-website-id': siteId });

            modal.close();
            body.innerHTML = old; // restore picker body if it stays open elsewhere
            return;
          }
        }
      }

      // Case B: append to selected component or to canvas root
      let added = null as any;
      const sel = ed.getSelected();
      if (sel?.append) {
        added = sel.append(html);
      } else {
        added = ed.addComponents(html);
      }

      // Normalize to array of components
      const comps = Array.isArray(added) ? added : [added];

      // Try to find the inserted <form> component(s) to mirror the attribute
      // (This ensures the trait panel shows it and it persists on re-serialize)
      const insertedForms = [
        ...comps.flatMap((c: any) => (c.find ? c.find('form') : [])),
        ...wrapper.find('form'), // fallback scan if needed
      ];

      // If there are multiple forms, set the attribute on each
      insertedForms.forEach((fc: any) => {
        try {
          fc.addAttributes?.({ 'data-website-id': siteId });
        } catch (_) {
          /* no-op */
        }
      });

      modal.close();
      body.innerHTML = old; // restore picker content if appropriate
    } catch (err) {
      console.error(err);
      body.innerHTML = old;
    }
  });
}
